import { Chip } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import type { ContractDetail } from '@/api/contracts';
import { contractStatusTone, statusLabel, roleLabel, directionLabel } from '@/pages/contracts/contract-format';
import { DetailHeader } from '@/components/detail-header';
import { ContractActions } from './contract-actions';
import { ContractEditDrawer } from './contract-edit-drawer';

/**
 * Contract detail identity header — canonical `DetailHeader`: breadcrumb to the
 * list, the `contract_number_id` as title, the status StatusPill (dominant) plus
 * subtle role/direction/SLA/NDA Chips as meta, and status-gated lifecycle actions
 * alongside an Edit drawer for non-destructive field changes.
 */
export function ContractDetailHeader({ detail }: { detail: ContractDetail }) {
  const title = detail.contract_number_id || `#${detail.contract_id}`;
  return (
    <DetailHeader
      parent={{ label: 'Contracts', to: '/contracts' }}
      current={title}
      title={title}
      meta={
        <>
          <StatusPill tone={contractStatusTone(detail.contract_status)}>
            {statusLabel(detail.contract_status)}
          </StatusPill>
          <Chip appearance="subtle" tone="neutral" size="sm">
            {roleLabel(detail.contract_role)}
          </Chip>
          <Chip appearance="outline" tone="neutral" size="sm">
            {directionLabel(detail.contract_direction)}
          </Chip>
          {detail.sla_enable ? (
            <Chip appearance="outline" tone="primary" size="sm">
              SLA
            </Chip>
          ) : null}
          {detail.nda_enable ? (
            <Chip appearance="outline" tone="success" size="sm">
              NDA
            </Chip>
          ) : null}
        </>
      }
      actions={
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
          <ContractEditDrawer detail={detail} />
          <ContractActions detail={detail} />
        </div>
      }
    />
  );
}
